import * as React from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { brand } from "../theme";
import type { AssessmentSectionProgress } from "../types/assessment";

export const assessmentPageSx = {
  minHeight: "100vh",
  bgcolor: brand.page,
  p: { xs: 2, md: 3 },
} as const;

export const assessmentOuterSx = {
  width: "100%",
  maxWidth: 1240,
  mx: "auto",
} as const;

/** Sidebar column beside the question panel; stacks above it on small screens. */
export const assessmentLayoutSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "292px minmax(0, 1fr)" },
  alignItems: "start",
  gap: { xs: 2, md: 2.5 },
} as const;

export const assessmentPanelSx = {
  borderRadius: 3,
  border: `1px solid ${brand.border}`,
  bgcolor: brand.surface,
  overflow: "hidden",
  boxShadow: "0 18px 44px rgba(17,24,39,0.06)",
} as const;

export const assessmentContentSx = {
  p: { xs: 2, md: 2.5 },
} as const;

export function AssessmentHeader(props: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  /** Optional trailing content, e.g. a language switcher on pages rendered without the nav bar. */
  actions?: React.ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        p: { xs: 1.75, md: 2 },
        mb: { xs: 2, md: 2.5 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              bgcolor: brand.ink,
              display: "grid",
              placeItems: "center",
              color: brand.onInkPrimary,
              flexShrink: 0,
              boxShadow: "0 10px 24px rgba(17,24,39,0.18)",
            }}
          >
            {props.icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                color: brand.textPrimary,
                lineHeight: 1.1,
              }}
            >
              {props.title}
            </Typography>
            <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.35 }}>
              {props.subtitle}
            </Typography>
          </Box>
        </Stack>

        {props.actions ? <Box sx={{ flexShrink: 0 }}>{props.actions}</Box> : null}
      </Stack>
    </Paper>
  );
}

/* ---------------- progress sidebar ---------------- */

function pct(done: number, total: number) {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, (done / total) * 100));
}

function SectionRow(props: {
  section: AssessmentSectionProgress;
  countLabel: (done: number, total: number) => string;
  skippedLabel: (skipped: number) => string;
}) {
  const { section, countLabel, skippedLabel } = props;
  const done = section.answered + section.skipped;
  const complete = section.total > 0 && done >= section.total;
  const value = pct(done, section.total);

  return (
    <Box
      sx={{
        position: "relative",
        pl: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: section.active ? brand.surfaceSunken : "transparent",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: 3,
          bgcolor: section.active ? brand.accent : "transparent",
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            bgcolor: complete ? brand.ink : "transparent",
            border: complete ? "none" : `2px solid ${brand.borderStrong}`,
            color: brand.onInkPrimary,
          }}
        >
          {complete && <CheckRoundedIcon sx={{ fontSize: 12 }} />}
        </Box>

        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: section.active ? 900 : 800,
            fontSize: 13,
            lineHeight: 1.3,
            color: section.active ? brand.textPrimary : brand.textSecondary,
          }}
        >
          {section.label}
        </Typography>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 12,
            whiteSpace: "nowrap",
            color: complete ? brand.textPrimary : brand.textMuted,
          }}
        >
          {countLabel(done, section.total)}
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={value}
        aria-label={section.label}
        sx={{
          mt: 0.9,
          ml: 3.25,
          height: 4,
          borderRadius: 20,
          bgcolor: brand.border,
          "& .MuiLinearProgress-bar": {
            borderRadius: 20,
            bgcolor: complete ? brand.ink : brand.accent,
          },
        }}
      />

      {section.skipped > 0 && (
        <Typography
          sx={{
            ml: 3.25,
            mt: 0.5,
            fontSize: 11,
            fontWeight: 800,
            color: brand.textFaint,
          }}
        >
          {skippedLabel(section.skipped)}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Progress rail for an assessment: overall completion plus a per-section
 * breakdown of what is answered and what is still open.
 */
export function AssessmentSidebar(props: {
  title: string;
  sections: AssessmentSectionProgress[];
  /** Overall answered + skipped across every section. */
  completed: number;
  total: number;
  overallLabel: (done: number, total: number) => string;
  remainingLabel: (remaining: number) => string;
  sectionsHeading: string;
  countLabel: (done: number, total: number) => string;
  skippedLabel: (skipped: number) => string;
  emptyLabel: string;
}) {
  const {
    title,
    sections,
    completed,
    total,
    overallLabel,
    remainingLabel,
    sectionsHeading,
    countLabel,
    skippedLabel,
    emptyLabel,
  } = props;

  const remaining = Math.max(0, total - completed);

  return (
    <Paper
      elevation={0}
      component="aside"
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        overflow: "hidden",
        boxShadow: "0 18px 44px rgba(17,24,39,0.06)",
        position: { md: "sticky" },
        top: { md: 24 },
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.25 }, bgcolor: brand.ink, color: brand.onInkPrimary }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 11,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: brand.onInkFaint,
          }}
        >
          {title}
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 0.75 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 30, lineHeight: 1 }}>
            {completed}
          </Typography>
          <Typography sx={{ fontWeight: 900, fontSize: 16, color: brand.onInkMuted }}>
            / {total}
          </Typography>
        </Stack>

        <Typography sx={{ fontSize: 12, fontWeight: 800, color: brand.onInkMuted, mt: 0.35 }}>
          {overallLabel(completed, total)}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={pct(completed, total)}
          sx={{
            mt: 1.25,
            height: 6,
            borderRadius: 20,
            bgcolor: "rgba(255,255,255,0.16)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 20,
              bgcolor: brand.accent,
            },
          }}
        />
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 1.75 } }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 11,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: brand.textFaint,
            px: 1.5,
            mb: 0.75,
          }}
        >
          {sectionsHeading}
        </Typography>

        {sections.length === 0 ? (
          <Typography sx={{ px: 1.5, fontSize: 13, color: brand.textMuted, fontWeight: 700 }}>
            {emptyLabel}
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {sections.map((section) => (
              <SectionRow
                key={section.key}
                section={section}
                countLabel={countLabel}
                skippedLabel={skippedLabel}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          px: { xs: 2, md: 2.25 },
          py: 1.5,
          borderTop: `1px solid ${brand.borderSoft}`,
          bgcolor: brand.surfaceMuted,
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 12,
            color: remaining === 0 ? brand.textPrimary : brand.accentStrong,
          }}
        >
          {remainingLabel(remaining)}
        </Typography>
      </Box>
    </Paper>
  );
}

export function AssessmentSectionIntro(props: {
  eyebrow: string;
  title: string;
  subtitle: string;
}) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surfaceMuted,
        p: { xs: 2, md: 2.5 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          bgcolor: brand.accent,
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: brand.accentStrong,
          mb: 0.9,
        }}
      >
        {props.eyebrow}
      </Typography>

      <Typography
        component="h2"
        sx={{
          fontWeight: 900,
          color: brand.textPrimary,
          fontSize: { xs: 24, md: 30 },
          lineHeight: 1.12,
        }}
      >
        {props.title}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: brand.textSecondary,
          mt: 1,
          maxWidth: 760,
          lineHeight: 1.65,
        }}
      >
        {props.subtitle}
      </Typography>
    </Box>
  );
}

export const questionCardSx = {
  borderRadius: 3,
  border: `1px solid ${brand.border}`,
  bgcolor: brand.surface,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(17,24,39,0.045)",
} as const;

export const questionHeaderSx = {
  p: { xs: 2, md: 2.25 },
  borderBottom: `1px solid ${brand.borderSoft}`,
  bgcolor: brand.surface,
} as const;

export const questionBodySx = {
  p: { xs: 1.5, md: 2 },
} as const;

export const questionLabelSx = {
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: 1,
  color: brand.accentStrong,
  textTransform: "uppercase",
} as const;

export const answerGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(3, minmax(0, 1fr))",
  },
  gap: 1,
} as const;

export function getAnswerOptionSx(selected: boolean, disabled = false) {
  return {
    m: 0,
    border: "1px solid",
    borderColor: selected ? brand.ink : brand.border,
    bgcolor: selected ? brand.surfaceSunken : brand.surface,
    borderRadius: 2,
    px: 1.25,
    py: 1.15,
    minHeight: 74,
    alignItems: "center",
    transition: "border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
    opacity: disabled && !selected ? 0.55 : 1,
    boxShadow: selected ? `inset 0 0 0 1px ${brand.ink}` : "none",
    "&:hover": {
      borderColor: selected ? brand.ink : brand.textFaint,
      bgcolor: selected ? brand.surfaceSunken : brand.surfaceMuted,
    },
    "& .MuiRadio-root, & .MuiCheckbox-root": {
      color: selected ? brand.ink : brand.textFaint,
    },
    "& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": {
      color: brand.ink,
    },
  } as const;
}

export const assessmentPrimaryButtonSx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  bgcolor: brand.ink,
  boxShadow: "none",
  px: 2.2,
  py: 1.1,
  "&:hover": { bgcolor: brand.inkDeep, boxShadow: "none" },
} as const;

export const assessmentOutlineButtonSx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  borderColor: brand.borderStrong,
  color: brand.textPrimary,
  bgcolor: brand.surface,
  px: 2.2,
  py: 1.1,
  "&:hover": { borderColor: brand.textFaint, bgcolor: brand.surfaceMuted },
} as const;
